/**
 * fetch() with a timeout.
 *
 * The browser `fetch` has no default timeout: if the backend hangs or a read
 * stalls, the promise never settles and the UI sits in a loading state forever
 * with no error. `fetchWithTimeout` aborts the request after `timeoutMs` and
 * rejects with a clear error so callers can show a failure instead of spinning.
 *
 * Honors a caller-supplied `init.signal` by racing it alongside the internal
 * timeout controller.
 */

export const DEFAULT_FETCH_TIMEOUT_MS = 30_000

export class FetchTimeoutError extends Error {
  constructor(url: string, timeoutMs: number) {
    super(`Request to ${url} timed out after ${timeoutMs}ms`)
    this.name = 'FetchTimeoutError'
  }
}

export async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit = {},
  timeoutMs: number = DEFAULT_FETCH_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)

  // If the caller passed their own signal, abort our request when theirs fires.
  if (init.signal) {
    if (init.signal.aborted) controller.abort()
    else init.signal.addEventListener('abort', () => controller.abort(), { once: true })
  }

  try {
    return await fetch(input, { ...init, signal: controller.signal })
  } catch (err) {
    // Distinguish our timeout from a caller-initiated abort or a network error.
    if (controller.signal.aborted && !init.signal?.aborted) {
      throw new FetchTimeoutError(String(input), timeoutMs)
    }
    throw err
  } finally {
    clearTimeout(timer)
  }
}
